import TextField, { TextAreaField } from "@/components/common/text-field";
import Tab from "@/components/ui/tab";
import { useHeaderButton } from "@/contexts/header-button-context";
import { Button, Heading, Icon, useToast } from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useState } from "react";
import PermissionsList from "../permissions-list";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { type NewRoleFormData, schema } from "@/schemas/roles/new-roles";
import UsersList from "./users-list";
import { useGetPermissions, useStoreRole } from "@/queries/roles";
import { useNavigate } from "react-router";
import type { AxiosError } from "axios";
import SearchInput from "@/components/common/search-input";
import PermissionType from "../permission-type";
import AssignUserDropdown from "../details/assign-user-dropdown";
import type { UserType } from "@/types/entities/users";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";

const NewRole = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuthorization();
  const canAssingRoleToUsers = hasPermission(Permissions.users["assign-role"]);

  const { data: permissions, isPending: isLoadingPermissions } =
    useGetPermissions();
  const { mutateAsync, isPending: isLoading } = useStoreRole();

  const [permissionsToSave, setPermissionsToSave] = useState<
    {
      name: string;
      id: string;
    }[]
  >([]);

  const handleCheckboxChange = (id: string) => {
    setPermissionsToSave((prev) => {
      const exists = prev.some((permission) => permission.id === id);
      if (exists) {
        return prev.filter((permission) => permission.id !== id);
      } else {
        const permission = permissions?.find((p) => p.id === id);
        if (permission) {
          return [...prev, { name: permission.name, id: permission.id }];
        }
        return prev;
      }
    });
  };

  const [localSearchQuery, setLocalSearchQuery] = useState<string>("");

  const permissionsMap = useMemo(() => {
    if (!permissions) return {};

    return permissions.reduce<
      Record<
        string,
        {
          id: string;
          name: string;
          display_name: string;
          group_name: string;
        }[]
      >
    >((acc, permission) => {
      acc[permission.group_name] = [
        ...(acc[permission.group_name] || []),
        permission,
      ];
      return acc;
    }, {});
  }, [permissions]);

  const filterPermissions = useMemo(() => {
    if (!permissionsMap) return {};
    const filteredPermissions = Object.entries(permissionsMap).reduce(
      (acc, [groupName, permissions]) => {
        const filtered = permissions.filter(
          (permission) =>
            permission.display_name
              .toLowerCase()
              .includes(localSearchQuery.toLowerCase()) ||
            permission.name
              .toLowerCase()
              .includes(localSearchQuery.toLowerCase()) ||
            permission.group_name
              .toLowerCase()
              .includes(localSearchQuery.toLowerCase()),
        );
        if (filtered.length > 0) {
          acc[groupName] = filtered;
        }
        return acc;
      },
      {} as Record<
        string,
        {
          id: string;
          name: string;
          display_name: string;
          group_name: string;
        }[]
      >,
    );

    return filteredPermissions;
  }, [permissionsMap, localSearchQuery]);

  const roleTypeByPermissions = useMemo(() => {
    let type: "admin" | "editor" | "reader" | "empty" = "empty";
    if (permissionsToSave.length === 0) {
      return type;
    }
    if (
      permissionsToSave.some((permission) => permission.name.includes("read"))
    ) {
      type = "reader";
    }
    if (
      permissionsToSave.some((permission) => {
        return (
          permission.name.includes("update") ||
          permission.name.includes("edit") ||
          permission.name.includes("create")
        );
      })
    ) {
      type = "editor";
    }
    if (
      permissionsToSave.some((permission) => {
        return (
          permission.name.includes("roles:create") ||
          permission.name.includes("roles:update") ||
          permission.name.includes("roles:delete") ||
          permission.name.includes("roles:assign-permissions") ||
          permission.name.includes("users:delete") ||
          permission.name.includes("users:assign-role") ||
          permission.name.includes("users:activate") ||
          permission.name.includes("users:create") ||
          permission.name.includes("users:update") ||
          permission.name.includes("users:reset-password")
        );
      })
    ) {
      type = "admin";
    }
    if (type === "empty") {
      type = "editor"; // Default to editor if no permissions match
    }
    return type;
  }, [permissionsToSave]);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isValid },
  } = useForm<NewRoleFormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const [name, description] = watch(["name", "description"]);

  const [activeTab, setActiveTab] = useState<"users" | "permissions">(
    "permissions",
  );

  const { setComp } = useHeaderButton();

  useEffect(() => {
    setComp(
      <Button
        variant="default"
        size="xs"
        type="submit"
        form="new-role-form"
        disabled={!isValid || permissionsToSave.length === 0 || isLoading}
      >
        Activar rol
        {isLoading && (
          <Icon
            type="far"
            name="spinner-third"
            className="animate-spin text-current"
          />
        )}
      </Button>,
    );

    return () => {
      setComp(null);
    };
  }, [setComp, isValid, isLoading, permissionsToSave]);

  const [selectedUsersToAssign, setSelectedUsersToAssign] = useState<
    UserType[]
  >([]);

  const onUnassign = (userId: string) => {
    setSelectedUsersToAssign((prev) => {
      return prev.filter((user) => user.keycloak_id !== userId);
    });
  };

  const onSubmit = async (data: NewRoleFormData) => {
    if (permissionsToSave.length === 0) {
      setError("name", {
        type: "manual",
        message: "Debes seleccionar al menos un permiso.",
      });
      return;
    }
    try {
      await mutateAsync({
        ...data,
        permissions: permissionsToSave.map((p) => p.id),
        type: roleTypeByPermissions,
        ...(canAssingRoleToUsers &&
          selectedUsersToAssign.length > 0 && {
            users: selectedUsersToAssign
              .map((user) => user.keycloak_id || "")
              .filter(Boolean), // Ensure no empty keycloak_id
          }),
      });
      showToast({
        title: "Rol creado correctamente",
        description: "El rol ha sido creado exitosamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
      navigate("/roles");
    } catch (error) {
      const e = error as AxiosError<{ message: string }>;
      if (
        e.status === 409 &&
        e.response?.data.message === "Role already exists"
      ) {
        setError("name", {
          type: "manual",
          message: "El nombre del rol ya existe. Por favor, elige otro.",
        });
      }
      showToast({
        title: "Error al crear el rol",
        description: "Ha ocurrido un error al crear el rol.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  return (
    <form
      className="h-full grid grid-cols-1 desktop:grid-cols-2 gap-8"
      onSubmit={handleSubmit(onSubmit)}
      id="new-role-form"
    >
      <div className="h-full w-full flex flex-col">
        <Heading variant="h5">Datos del rol</Heading>
        <div className="flex flex-col gap-6 pt-4">
          <TextField
            id="name"
            label="Nombre"
            placeholder="Ingrese el nombre del rol"
            className="w-full"
            variant="lg"
            required
            {...register("name")}
            maxLength={125}
            limit={125}
            count={name.length}
            error={errors.name?.message || ""}
          />
          <TextAreaField
            id="description"
            label="Descripción"
            placeholder="Ingrese una descripción del rol"
            className="w-full"
            rows={4}
            required
            {...register("description")}
            maxLength={255}
            limit={255}
            count={description.length}
            error={errors.description?.message || ""}
          />
        </div>
        <div className="flex flex-col gap-2 pt-8">
          <Heading variant="h5">Tipo de rol</Heading>
          <p className="leading-3xl text-body font-normal">
            El tipo de rol está determinado por los permisos activos para los
            usuarios. Aquí podrás ver qué tipo de rol tendrán los usuarios que
            añadas según los permisos que vayas seleccionando.
          </p>
          <div className="pt-6">
            <PermissionType
              name="Administrador"
              description="Permisos de edición sobre Roles y Usuarios"
              isActive={roleTypeByPermissions === "admin"}
              isTypeEmpty={roleTypeByPermissions === "empty"}
            />
            <PermissionType
              name="Editor"
              description="Permisos de edición sobre Procesos, Flujos y demás."
              isActive={roleTypeByPermissions === "editor"}
              isTypeEmpty={roleTypeByPermissions === "empty"}
            />
            <PermissionType
              name="Lector"
              description="Permisos de lectura en todas las partes del sistema."
              isActive={roleTypeByPermissions === "reader"}
              isTypeEmpty={roleTypeByPermissions === "empty"}
              showDivider={false}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col w-full h-fit shadow-custom bg-neutral-object border-neutral border rounded-xl overflow-hidden [@media(min-height:800px)]:max-h-[calc(100vh-150px)] max-h-[678px]">
        <div className="flex justify-between items-center px-3">
          <div className="relative pt-3">
            <Tab
              active={activeTab === "permissions"}
              onClick={() => setActiveTab("permissions")}
            >
              Permisos
            </Tab>
            {canAssingRoleToUsers && (
              <Tab
                active={activeTab === "users"}
                onClick={() => setActiveTab("users")}
              >
                Usuarios
              </Tab>
            )}
          </div>
          {activeTab === "permissions" && (
            <SearchInput
              variant="xs"
              placeholder="Buscar permisos"
              onChange={({ target }) => {
                setLocalSearchQuery(target.value);
              }}
            />
          )}
          {activeTab === "users" && canAssingRoleToUsers && (
            <div className="pt-3 pb-2">
              <AssignUserDropdown
                onAddUser={(user) => {
                  setSelectedUsersToAssign((prev) => {
                    return [...prev, user as UserType];
                  });
                }}
                selectedUsers={selectedUsersToAssign}
              />
            </div>
          )}
        </div>
        {activeTab === "users" && (
          <UsersList onUnassign={onUnassign} users={selectedUsersToAssign} />
        )}
        {activeTab === "permissions" && (
          <PermissionsList
            isLoading={isLoadingPermissions}
            items={filterPermissions}
            handleCheckboxChange={handleCheckboxChange}
            selectedItems={permissionsToSave.map((p) => p.id)}
          />
        )}
      </div>
    </form>
  );
};

export default NewRole;
